import {ChangeDetectorRef, Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgbModal, NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';

import {UnitDto} from '../../../../generated-model/models';
import {UnitService} from './unit-service';
import {UnitModal} from './unit-modal';
import {ConfirmDialogService} from '../../../shared/ui/confirm-dialog/confirm-dialog.service';
import {firstValueFrom} from 'rxjs';

import {resolveTotalElements} from '../../../shared/commons/pagination.util';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../shared/components/pagesize-select/pagesize-select.component';
import {StatusBadgeComponent} from '../../../shared/components/status-badge/status-badge.component';
import {SearchIconComponent} from '../../../shared/components/search-icon/search-icon.component';

@Component({
  selector: 'app-unit',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent, StatusBadgeComponent, SearchIconComponent],
  templateUrl: './unit.html',
  styleUrl: './unit.scss',
})
export class UnitComponent implements OnInit {
  private readonly service = inject(UnitService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(NgbModal);
  private readonly confirmDialogService = inject(ConfirmDialogService);

  protected readonly searchQuery = signal('');
  protected readonly stateFilter = signal<number | null>(null);
  protected readonly isLoading = signal(false);
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly collectionSize = signal(0);
  protected readonly displayedItems = signal<UnitDto[]>([]);

  private totalElements = 0;

  constructor() {
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.service.getAllUnits(this.page() - 1, this.pageSize(), this.searchQuery(), this.stateFilter()).subscribe({
      next: (data) => {
        this.displayedItems.set(data.content);
        this.totalElements = resolveTotalElements(data);
        this.collectionSize.set(this.totalElements);
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading.set(false);
        this.cdr.detectChanges();
      }
    });
  }

  onSearchChange(): void {
    this.page.set(1);
    this.load();
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.stateFilter.set(null);
    this.onSearchChange();
  }

  openAddModal(): void {
    const modalRef = this.modalService.open(UnitModal, {size: 'lg'});
    modalRef.componentInstance.onSave = async (result: UnitDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed) {
        return false;
      }
      try {
        await firstValueFrom(this.service.createUnit(result));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openEditModal(item: UnitDto): void {
    const modalRef = this.modalService.open(UnitModal, {size: 'lg'});
    modalRef.componentInstance.unit = item;
    modalRef.componentInstance.onSave = async (result: UnitDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed || result.id == null) {
        return false;
      }
      try {
        await firstValueFrom(this.service.updateUnit(result.id, result));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openViewModal(item: UnitDto): void {
    const modalRef = this.modalService.open(UnitModal, {size: 'lg'});
    modalRef.componentInstance.unit = item;
    modalRef.componentInstance.viewOnly = true;
  }

  async delete(id: number): Promise<void> {
    const confirmed = await this.confirmDialogService.confirmDelete('คุณต้องการลบข้อมูลหน่วยนับนี้ใช่หรือไม่?');
    if (!confirmed) {
      return;
    }
    this.service.deleteUnit(id).subscribe(() => this.load());
  }
}
